/**
 * The Chats page's side column (ADR 0024 §3): the person's own arrangement of
 * Fan panel blocks and widgets, in a Top area and a Dock under it. Beside the
 * chat on a desktop (resizable, hideable: the Fan panel's own width key); a
 * slide-over sheet on phones.
 */
import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { SideFrame } from '../chat/ChatView'
import { FanIdentity, ListsBlock, NotesBlock, SpendingBlock } from '../chat/fanBlocks'
import { useSectionsOpen, type SectionsOpen } from '../chat/sectionsOpen'
import type { FanData } from '../chat/useFan'
import { useMediaUrl } from '../chat/media'
import { FeatherIcon } from '../components/FeatherIcon'
import { useWorkspace } from '../workspace/WorkspaceProvider'
import { noteSuggestions, useAiSession, type AiInsight } from './aiSession'
import { useChatPrefs } from './chatPrefs'
import { AiReasoningBlock, AiSummaryBlock, OnlineSpendersBlock, SpendAcrossBlock, type OpenChat } from './sideBlocks'
import { inArea, SIDE_SETTINGS_KEY, SIDE_WIDGETS, sideColumnFrom, type SideBlock, type SideBlockId } from './sideLayout'
import { ActiveDock, ActivityDock, FeedDock } from './sideDock'
import { SideEditor } from './SideEditor'

export interface SideColumnProps {
  accountId: string
  fanId: string
  fan: FanData
  frame: SideFrame
  aiStatus?: string | null
  onOpenChat?: OpenChat
  /** The Workspace page (a dock widget's expand button). */
  onOpenWorkspace?: () => void
}

/** The person's arrangement, from their Workspace settings. */
function useSideBlocks() {
  const ws = useWorkspace()
  const raw = ws.settings[SIDE_SETTINGS_KEY]
  return useMemo(() => sideColumnFrom(raw), [raw])
}

export function SideColumn({ accountId, fanId, fan, frame, aiStatus, onOpenChat, onOpenWorkspace }: SideColumnProps) {
  const { t } = useTranslation('chat')
  const ws = useWorkspace()
  const { names } = useChatPrefs()
  const blocks = useSideBlocks()
  const sections = useSectionsOpen()
  const [editing, setEditing] = useState(false)
  // The server keeps one settings object: editing waits until it is read, or a save would replace it.
  const canEdit = ws.enabled && ws.prefsLoaded
  const available = (id: SideBlockId) => ws.enabled || !SIDE_WIDGETS.has(id)
  const saveBlocks = (next: SideBlock[] | null) => ws.updateSettings({ [SIDE_SETTINGS_KEY]: next ? { blocks: next } : null })
  const shown = blocks.filter((b) => b.shown && available(b.id))
  const wantsAi = shown.some((b) => b.id === 'aiReasoning' || b.id === 'aiSummary' || b.id === 'notes')
  const ai = useAiSession(accountId, fanId, wantsAi)
  const name = names[fanId] || fan.profile?.name || ''
  const top = inArea(shown, 'top')
  const dock = inArea(shown, 'dock')

  const tools = frame.phone ? null : (
    <>
      {canEdit && (
        <button type="button" className="rc-icon-btn" title={t('side.edit.open')} aria-label={t('side.edit.open')} onClick={() => setEditing(true)}>
          <FeatherIcon name="pencil" size={13} />
        </button>
      )}
      {frame.sheet ? (
        <button type="button" className="rc-icon-btn" title={t('common.close')} aria-label={t('common.close')} onClick={frame.onHide}>
          <FeatherIcon name="x" size={15} />
        </button>
      ) : (
        <button type="button" className="rc-icon-btn" title={t('fan.hidePanel')} aria-label={t('fan.hidePanel')} onClick={frame.onHide}>
          <FeatherIcon name="panel-right-close" size={14} />
        </button>
      )}
    </>
  )
  const identityFirst = top[0]?.id === 'overview'
  const block = (id: SideBlockId): ReactNode =>
    renderBlock(id, { accountId, fanId, fan, name, aiStatus, onOpenChat, onOpenWorkspace, sections, ai, tools: id === 'overview' && identityFirst ? tools : undefined, accounts: ws.accounts })

  const body = editing ? (
    <>
      <div className="rc-side-bar rc-side-bar--edit">
        <span className="rc-side-bar-title">{t('side.edit.title')}</span>
        <button type="button" className="rc-side-done" onClick={() => setEditing(false)}>
          {t('side.edit.done')}
        </button>
      </div>
      <SideEditor blocks={blocks} available={available} onChange={saveBlocks} />
    </>
  ) : (
    <>
      {!identityFirst && <div className="rc-side-bar">{tools}</div>}
      <div className="rc-side-top rc-fan-scroll" role="region" aria-label={t('fan.info')}>
        {top.map((b) => (
          <Fragment key={b.id}>{block(b.id)}</Fragment>
        ))}
      </div>
      {dock.length > 0 && (
        <div className="rc-side-dock rc-fan-scroll" role="region" aria-label={t('side.dock')}>
          {dock.map((b) => (
            <Fragment key={b.id}>{block(b.id)}</Fragment>
          ))}
        </div>
      )}
    </>
  )

  // Phones: the Fan sheet (ADR 0035) — the fan on top, then the person's blocks; widgets and editing stay on desktop.
  if (frame.phone)
    return (
      <div className="rc-side rc-side--sheet rc-side--phone">
        {!identityFirst && <PhoneFanTop accountId={accountId} fan={fan} name={name || fanId} />}
        <div className="rc-side-top" role="region" aria-label={t('fan.info')}>
          {top.map((b) => (
            <Fragment key={b.id}>{block(b.id)}</Fragment>
          ))}
        </div>
      </div>
    )
  if (frame.sheet) return <div className="rc-side rc-side--sheet">{body}</div>
  return (
    <aside className="rc-fan rc-side" style={frame.width ? { width: frame.width } : undefined}>
      {frame.onResizeStart && <span className={`rc-fan-resize ${frame.resizing ? 'is-active' : ''}`} onPointerDown={frame.onResizeStart} title={t('fan.dragToResize')} />}
      {body}
    </aside>
  )
}

interface BlockContext {
  accountId: string
  fanId: string
  fan: FanData
  name: string
  aiStatus?: string | null
  onOpenChat?: OpenChat
  onOpenWorkspace?: () => void
  sections: SectionsOpen
  ai: AiInsight
  tools?: ReactNode
  accounts: ReturnType<typeof useWorkspace>['accounts']
}

function renderBlock(id: SideBlockId, c: BlockContext): ReactNode {
  const dock = { accountId: c.accountId, fanId: c.fanId, sections: c.sections, onOpenChat: c.onOpenChat, onOpenWorkspace: c.onOpenWorkspace }
  switch (id) {
    case 'overview':
      return <FanIdentity accountId={c.accountId} fanId={c.fanId} fan={c.fan} name={c.name} tools={c.tools} />
    case 'spending':
      return <SpendingBlock fan={c.fan} sections={c.sections} />
    case 'spendAcross':
      return <SpendAcrossBlock accountId={c.accountId} fanId={c.fanId} fanName={c.name || c.fanId} accounts={c.accounts} sections={c.sections} onOpenChat={c.onOpenChat} />
    case 'lists':
      return <ListsBlock fan={c.fan} sections={c.sections} />
    case 'notes':
      return <NotesBlock fan={c.fan} sections={c.sections} suggestions={noteSuggestions(c.ai.profile)} />
    case 'aiReasoning':
      return <AiReasoningBlock ai={c.ai} aiStatus={c.aiStatus} sections={c.sections} />
    case 'aiSummary':
      return <AiSummaryBlock ai={c.ai} sections={c.sections} />
    case 'onlineSpenders':
      return <OnlineSpendersBlock accountId={c.accountId} sections={c.sections} onOpenChat={c.onOpenChat} />
    case 'activity':
      return <ActivityDock {...dock} />
    case 'feed':
      return <FeedDock {...dock} />
    case 'active':
      return <ActiveDock {...dock} />
    default:
      return null
  }
}

/** The Fan sheet's top when the person's first block is not the Overview (which shows the fan itself). */
function PhoneFanTop({ accountId, fan, name }: { accountId: string; fan: FanData; name: string }) {
  const avatar = useMediaUrl(accountId, fan.profile?.avatar)
  return (
    <div className="rc-fan-id">
      <div className="rc-fan-who">
        <span className="rc-avatar rc-avatar--40">{avatar.url ? <img src={avatar.url} alt="" /> : name.slice(0, 1).toUpperCase()}</span>
        <div className="rc-fan-names">
          <span className="rc-fan-name">{name}</span>
          {fan.profile?.username && <span className="rc-muted">@{fan.profile.username}</span>}
        </div>
      </div>
    </div>
  )
}
